import { ArrowLeft, Calendar, ChevronDown, Pencil, UserPlus } from "lucide-react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useMemo } from "react";
import { db, storage } from "../firebase";
import { doc, updateDoc, getDoc, deleteDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import SavePopup from "./SavePopup";
import Navbar from "../components/Navbar";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { collection, query, where, getDocs, addDoc } from "firebase/firestore";
import { addTimelineEvent } from "../services/timelineService";
import { CASE_STATUSES, CASE_STAGES, getStatusCollection } from "../utils/caseStatus";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import SelectMenu from "../components/crm/SelectMenu";
const AUTHORITY_DEPARTMENTS = [
  "Residential",
  "Institutional",
  "IT",
  "HRD",
  "Water",
  "Urban Services",
  "Land",
  "Builders",
  "Group Housing",
  "Sewar",
  "Health",
  "University",
  "Finance",
  "Commercial",
  "6% Abadi Plot",
  "Others",
];

const COUNTER_AFFIDAVIT_STATUSES = [
  "Not Initiated",
  "Notice Received - Awaiting Instructions",
  "Para-wise Comments (PWC) Requested",
  "PWC Received from Department",
  "Drafting Under Preparation",
  "Draft Sent for Department Approval",
  "Vetted & Under Execution",
  "Filed (Physical/e-Filing)",
  "Served on Applicant/Parties",
  "Rejoinder Received",
];

const CustomSelect = ({ value, onChange, options, placeholder = "Select an option" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-gray-900 rounded-lg py-2 px-4 text-left flex items-center justify-between pr-10"
      >
        <span className={`truncate ${!value ? "text-gray-400" : "text-white"}`}>
          {value || placeholder}
        </span>
        <ChevronDown className={`ml-2 flex-shrink-0 transition-transform text-gray-400 ${isOpen ? "rotate-180" : ""}`} size={20} />
      </button>
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-gray-900 border border-gray-700 rounded-lg shadow-xl max-h-64 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              className="w-full px-4 py-3 text-left text-white hover:bg-[#1E1E45] transition-colors border-b border-gray-800 last:border-b-0 text-sm"
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Default Reminder Info Modal Component
const DefaultReminderInfoModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1A1535] rounded-xl border border-gray-700 max-w-md w-full">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-white text-center mb-4">Default Reminders</h3>
          <div className="flex flex-col items-center space-y-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-300 text-center leading-relaxed">
              Case hearing reminder is set at 8 AM one day prior and next hearing date update reminder is set at 6 PM on the same day.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-8 py-3 bg-white hover:bg-gray-200 text-[#1A1535] font-semibold rounded-lg transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Relation options used for petitioner/respondent
const RELATION_OPTIONS = ["S/o", "D/o", "W/o"];

// Modal to pick S/o D/o W/o and enter the associated name
const RelationDialog = ({ isOpen, onClose, title, relation, relationName, onSave, namePlaceholder = "Enter name" }) => {
  const [localRelation, setLocalRelation] = useState(relation || "");
  const [localName, setLocalName] = useState(relationName || "");

  useEffect(() => {
    if (isOpen) {
      setLocalRelation(relation || "");
      setLocalName(relationName || "");
    }
  }, [isOpen, relation, relationName]);

  if (!isOpen) return null;

  const handleNameChange = (e) => {
    const val = e.target.value;
    if (/[^a-zA-Z\s.]/.test(val)) return;
    setLocalName(val);
  };

  const handleSave = () => {
    if (!localRelation) {
      onSave({ relation: "", relationName: "" });
    } else {
      onSave({ relation: localRelation, relationName: (localName || "").trim() });
    }
    onClose();
  };

  const handleClear = () => {
    setLocalRelation("");
    setLocalName("");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-[#1A1535] rounded-xl border border-gray-700 max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h3 className="text-xl font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors" type="button">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="text-gray-300 text-sm mb-2 block">Select relation type</label>
            <div className="flex flex-wrap gap-2">
              {RELATION_OPTIONS.map((opt) => {
                const active = localRelation === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setLocalRelation(active ? "" : opt)}
                    className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                      active
                        ? "bg-yellow-500 text-[#1A1535] border-yellow-500"
                        : "bg-transparent text-gray-300 border-gray-600 hover:border-yellow-500 hover:text-yellow-400"
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2">S/o – Son of · D/o – Daughter of · W/o – Wife of</p>
          </div>
          {localRelation && (
            <div>
              <label className="text-gray-300 text-sm mb-2 block">{localRelation} name</label>
              <input
                type="text"
                value={localName}
                onChange={handleNameChange}
                className="w-full bg-[#0A061E] text-white p-3 rounded-lg border border-gray-700 focus:border-blue-500 focus:ring-blue-500 text-sm"
                placeholder={namePlaceholder}
                autoFocus
              />
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-700">
          <button
            type="button"
            onClick={handleClear}
            className="text-sm text-gray-400 hover:text-red-400 transition-colors"
          >
            Clear
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 text-sm text-gray-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-6 py-2 bg-white hover:bg-gray-200 text-[#1A1535] font-semibold rounded-lg transition-colors text-sm"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Compact badge shown below the name input. Click to open dialog. Shows "+ Add Relation" when empty.
const RelationBadge = ({ relation, relationName, onOpen, onClear }) => {
  const hasValue = relation && relationName;
  if (!hasValue) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="text-sm text-yellow-500 hover:text-yellow-400 transition-colors whitespace-nowrap"
      >
        + Add Relation
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/15 border border-yellow-500/40 text-xs text-yellow-200 max-w-full">
      <span className="font-semibold">{relation}</span>
      <span className="text-gray-200 truncate">{relationName}</span>
      <button
        type="button"
        onClick={onOpen}
        title="Edit relation"
        className="text-yellow-400 hover:text-yellow-200 transition-colors flex-shrink-0"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={onClear}
        title="Remove relation"
        className="text-gray-400 hover:text-red-400 transition-colors flex-shrink-0"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// Add More Modal Component
const AddMoreModal = ({ isOpen, onClose, title, items, setItems, supportsRelation = false }) => {
  const makeEmpty = () => (supportsRelation ? { name: "", relation: "", relationName: "" } : { name: "" });

  const normalize = (item) => (supportsRelation
    ? { name: item?.name || "", relation: item?.relation || "", relationName: item?.relationName || "" }
    : { name: item?.name || "" });

  const [localItems, setLocalItems] = useState(items.length > 0 ? items.map(normalize) : [makeEmpty()]);
  const [relationDialogIndex, setRelationDialogIndex] = useState(-1);

  useEffect(() => {
    setLocalItems(items.length > 0 ? items.map(normalize) : [makeEmpty()]);
  }, [items, isOpen]);

  const addNewItem = () => {
    setLocalItems([...localItems, makeEmpty()]);
  };

  const removeItem = (index) => {
    setLocalItems(localItems.filter((_, i) => i !== index));
  };

  const updateItem = (index, value) => {
    const updated = [...localItems];
    updated[index] = { ...updated[index], name: value };
    setLocalItems(updated);
  };

  const setItemRelation = (index, relation, relationName) => {
    const updated = [...localItems];
    updated[index] = { ...updated[index], relation: relation || "", relationName: relationName || "" };
    setLocalItems(updated);
  };

  const handleSave = () => {
    const filtered = localItems
      .filter(item => item.name.trim() !== "")
      .map(item => supportsRelation
        ? { name: item.name.trim(), relation: item.relation || "", relationName: (item.relationName || "").trim() }
        : { name: item.name.trim() });
    setItems(filtered);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1A1535] rounded-xl border border-gray-700 max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h3 className="text-xl font-semibold text-white">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {localItems.map((item, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => updateItem(index, e.target.value)}
                  className="flex-1 bg-[#0A061E] text-white p-3 rounded-lg border border-gray-700 focus:border-blue-500 focus:ring-blue-500"
                  placeholder={`${title.includes("Advocate") ? (title.includes("Petitioner") ? "Enter petitioner advocate" : "Enter respondent advocate") : (title.includes("Petitioner") ? "Enter petitioner" : "Enter respondent")} ${index + 1}`}
                />
                {localItems.length > 1 && (
                  <button
                    onClick={() => removeItem(index)}
                    className="p-2 text-red-500 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2 pl-3">
                {index === localItems.length - 1 ? (
                  <button
                    onClick={addNewItem}
                    className="flex items-center gap-1 text-yellow-500 hover:text-yellow-400 transition-colors text-xs"
                    title="Add more"
                    type="button"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    <span>Add more</span>
                  </button>
                ) : (
                  <span />
                )}
                {supportsRelation && (
                  <RelationBadge
                    relation={item.relation || ""}
                    relationName={item.relationName || ""}
                    onOpen={() => setRelationDialogIndex(index)}
                    onClear={() => setItemRelation(index, "", "")}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center p-6 border-t border-gray-700">
          <button
            onClick={handleSave}
            className="px-8 py-3 bg-white hover:bg-gray-200 text-[#1A1535] font-semibold rounded-lg transition-colors"
          >
            Save
          </button>
        </div>
      </div>
      {supportsRelation && relationDialogIndex >= 0 && (
        <RelationDialog
          isOpen={relationDialogIndex >= 0}
          onClose={() => setRelationDialogIndex(-1)}
          title={`Set Relation${localItems[relationDialogIndex]?.name ? ` — ${localItems[relationDialogIndex].name}` : ""}`}
          relation={localItems[relationDialogIndex]?.relation || ""}
          relationName={localItems[relationDialogIndex]?.relationName || ""}
          onSave={({ relation, relationName }) => setItemRelation(relationDialogIndex, relation, relationName)}
          namePlaceholder="Enter father/mother name"
        />
      )}
    </div>
  );
};

function EditCaseDetails() {
  const { globalUser } = useAuth(); // Add this to get current user
  const [userMobile, setUserMobile] = useState(""); // Add state for userMobile
  const [isDepartmentVerified, setIsDepartmentVerified] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [caseData, setCaseData] = useState(null);
  const [formData, setFormData] = useState({
    caseNo: "",
    caseType: "",
    caseYear: "",
    caseFiledDate: "",
    nextHearingDate: "",
    previousHearingDate: "",
    courtName: "",
    partyContactNo: "",
    respondentName: "",
    petitionerName: "",
    petitionerRelation: "",
    petitionerRelationName: "",
    respondentRelation: "",
    respondentRelationName: "",
    files: [],
    owner: "",
    status: "",
    caseStage: "",
    allPetitioners: [],
    allRespondents: [],
    petitionerAdvocate: "",
    respondentAdvocate: "",
    allPetitionerAdvocates: [],
    allRespondentAdvocates: [],
    authorityDepartment: "",
    authorityDepartmentOther: "",
    properties: "",
    area: "",
    village: "",
    stayOrderDate: "",
    counterAffidavitStatus: "",
    counterAppealFiled: "",
    prayers: "",
    supremeCourtStage: "",
    supremeCourtCategory: "",
    supremeCourtSection: "",
  });
  const [showPopup, setShowPopup] = useState(false);
  const fileInputRef = useRef(null);
  const [showPetitionerModal, setShowPetitionerModal] = useState(false);
  const [showRespondentModal, setShowRespondentModal] = useState(false);
  const [showPetitionerAdvocateModal, setShowPetitionerAdvocateModal] = useState(false);
  const [showRespondentAdvocateModal, setShowRespondentAdvocateModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showPetitionerRelationDialog, setShowPetitionerRelationDialog] = useState(false);
  const [showRespondentRelationDialog, setShowRespondentRelationDialog] = useState(false);

  // Store original next hearing date to track changes (like Dart code line 64, 86)
  const [originalNextHearingDate, setOriginalNextHearingDate] = useState("");

  const todayDate = useMemo(() => {
    const current = new Date();
    current.setHours(0, 0, 0, 0);
    return current;
  }, []);

  const today = useMemo(() => {
    const year = todayDate.getFullYear();
    const month = String(todayDate.getMonth() + 1).padStart(2, "0");
    const day = String(todayDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, [todayDate]);

  // Helper to parse YYYY-MM-DD string to Date object for DatePicker
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split("-");
    if (!year || !month || !day) return null;
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  };

  // Helper to convert Date object to YYYY-MM-DD string for formData
  const formatDateToInput = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper function to format date as YYYY-MM-DD without timezone issues
  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to convert DD/MM/YYYY to YYYY-MM-DD
  const convertToInputFormat = (dateStr) => {
    if (!dateStr) return "";
    // If already in YYYY-MM-DD format, return as is
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }
    // If in DD/MM/YYYY format, convert to YYYY-MM-DD
    if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      const [day, month, year] = dateStr.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  };

  // Helper function to convert YYYY-MM-DD to DD/MM/YYYY
  const convertToDisplayFormat = (dateStr) => {
    if (!dateStr) return "";
    // If in YYYY-MM-DD format, convert to DD/MM/YYYY
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  };

  useEffect(() => {
    const fetchUserMobile = async () => {
      if (!globalUser?.uid) return;
      try {
        const userQuery = query(
          collection(db, "lawyers"),
          where("uid", "==", globalUser.uid)
        );
        const userDocSnapshot = await getDocs(userQuery);
        if (!userDocSnapshot.empty) {
          const userData = userDocSnapshot.docs[0].data();
          setUserMobile(userData.mobileWithCountryCode || "");
        } else {
          console.warn("User document not found for UID:", globalUser.uid);
          setUserMobile("");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };

    fetchUserMobile();
  }, [globalUser]);

  useEffect(() => {
    const fetchDeptVerification = async () => {
      if (!globalUser?.uid) {
        setIsDepartmentVerified(false);
        return;
      }
      try {
        const userCollectionName =
          localStorage.getItem("userType") === "client" ? "client" : "lawyers";
        const userDoc = await getDoc(doc(db, userCollectionName, globalUser.uid));
        setIsDepartmentVerified(
          userDoc.exists() ? Boolean(userDoc.data().departmentVerified) : false
        );
      } catch (err) {
        console.error("Error fetching department verification:", err);
        setIsDepartmentVerified(false);
      }
    };
    fetchDeptVerification();
  }, [globalUser]);

  useEffect(() => {
    const fetchCaseDetails = async () => {
      const pendingCaseDoc = doc(db, "pending", id);
      const pendingCaseSnapshot = await getDoc(pendingCaseDoc);
      const disposedCaseDoc = doc(db, "disposed", id);
      const disposedCaseSnapshot = await getDoc(disposedCaseDoc);

      if (pendingCaseSnapshot.exists()) {
        const data = pendingCaseSnapshot.data();
        setCaseData(data);
        console.log("Pending case data:", data);
        setFormData({
          caseNo: data.caseNo || "",
          caseType: data.caseType || "",
          caseYear: data.caseYear || "",
          caseFiledDate: convertToInputFormat(data.caseFiledDate || ""),
          nextHearingDate: convertToInputFormat(data.nextHearingDate || ""),
          previousHearingDate: convertToInputFormat(data.previousHearingDate || ""),
          courtName: data.courtName || "",
          partyContactNo: data.partyContactNo || "",
          respondentName: data.respondentName || "",
          petitionerName: data.petitionerName || "",
          petitionerRelation: data.petitionerRelation || "",
          petitionerRelationName: data.petitionerRelationName || "",
          respondentRelation: data.respondentRelation || "",
          respondentRelationName: data.respondentRelationName || "",
          files: data.files || [],
          owner: data.owner || "",
          status: data.status || "",
          caseStage: data.caseStage || "",
          allPetitioners: data.allPetitioners || [],
          allRespondents: data.allRespondents || [],
          petitionerAdvocate: data.petitionerAdvocate || "",
          respondentAdvocate: data.respondentAdvocate || "",
          allPetitionerAdvocates: data.allPetitionerAdvocates || [],
          allRespondentAdvocates: data.allRespondentAdvocates || [],
          authorityDepartment: data.authorityDepartment || "",
          authorityDepartmentOther: data.authorityDepartmentOther || "",
          properties: data.properties || "",
          area: data.area || "",
          village: data.village || "",
          stayOrderDate: convertToInputFormat(data.stayOrderDate || ""),
          counterAffidavitStatus: data.counterAffidavitStatus || "",
          counterAppealFiled: data.counterAppealFiled || "",
          prayers: data.prayers || "",
          supremeCourtStage: data.payload?.statusStage || "",
          supremeCourtCategory: data.payload?.category || "",
          supremeCourtSection: data.payload?.section || "",
        });
        // Store original next hearing date in DD/MM/YYYY format (like Dart code line 86)
        setOriginalNextHearingDate(data.nextHearingDate || "");
      } else if (disposedCaseSnapshot.exists()) {
        const data = disposedCaseSnapshot.data();
        setCaseData(data);
        setFormData({
          caseNo: data.caseNo || "",
          caseType: data.caseType || "",
          caseYear: data.caseYear || "",
          caseFiledDate: convertToInputFormat(data.caseFiledDate || ""),
          nextHearingDate: convertToInputFormat(data.nextHearingDate || ""),
          previousHearingDate: convertToInputFormat(data.previousHearingDate || ""),
          courtName: data.courtName || "",
          partyContactNo: data.partyContactNo || "",
          respondentName: data.respondentName || "",
          petitionerName: data.petitionerName || "",
          petitionerRelation: data.petitionerRelation || "",
          petitionerRelationName: data.petitionerRelationName || "",
          respondentRelation: data.respondentRelation || "",
          respondentRelationName: data.respondentRelationName || "",
          files: data.files || [],
          owner: data.owner || "",
          status: data.status || "",
          caseStage: data.caseStage || "",
          allPetitioners: data.allPetitioners || [],
          allRespondents: data.allRespondents || [],
          petitionerAdvocate: data.petitionerAdvocate || "",
          respondentAdvocate: data.respondentAdvocate || "",
          allPetitionerAdvocates: data.allPetitionerAdvocates || [],
          allRespondentAdvocates: data.allRespondentAdvocates || [],
          authorityDepartment: data.authorityDepartment || "",
          authorityDepartmentOther: data.authorityDepartmentOther || "",
          properties: data.properties || "",
          area: data.area || "",
          village: data.village || "",
          stayOrderDate: convertToInputFormat(data.stayOrderDate || ""),
          counterAffidavitStatus: data.counterAffidavitStatus || "",
          counterAppealFiled: data.counterAppealFiled || "",
          prayers: data.prayers || "",
          supremeCourtStage: data.payload?.statusStage || "",
          supremeCourtCategory: data.payload?.category || "",
          supremeCourtSection: data.payload?.section || "",
        });
        // Store original next hearing date in DD/MM/YYYY format (like Dart code line 86)
        setOriginalNextHearingDate(data.nextHearingDate || "");
      } else {
        console.error("No such case found!");
      }
    };

    fetchCaseDetails();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCaseFiledDateChange = (date) => {
    setFormData((prev) => ({ ...prev, caseFiledDate: formatDateToInput(date) }));
  };

  const handlePreviousHearingDateChange = (date) => {
    if (date) {
      const todayCopy = new Date();
      todayCopy.setHours(23, 59, 59, 999);
      if (date > todayCopy) {
        toast.error("Previous hearing date cannot be in the future");
        return;
      }
    }
    setFormData((prev) => ({ ...prev, previousHearingDate: formatDateToInput(date) }));
  };

  const handleNextHearingDateChange = (date) => {
    if (date) {
      const todayCopy = new Date();
      todayCopy.setHours(0, 0, 0, 0);
      if (date < todayCopy) {
        toast.error("Next hearing date cannot be in the past");
        return;
      }
      const prevDate = parseDate(formData.previousHearingDate);
      if (prevDate && date < prevDate) {
        toast.error("Next hearing date cannot be before the previous hearing date");
        return;
      }
    }
    setFormData((prev) => ({ ...prev, nextHearingDate: formatDateToInput(date) }));
  };

  const handleFileUpload = async (e) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      const uploadPromises = filesArray.map(async (file) => {
        const storageRef = ref(
          storage,
          `case-files/${Date.now()}-${file.name}`
        );
        const toastId = toast.loading(`Uploading ${file.name}...`);
        try {
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          toast.success(`${file.name} uploaded successfully!`, { id: toastId });
          return { url, fileName: file.name };
        } catch (error) {
          console.error("Upload Error:", error);
          toast.error(`Failed to upload ${file.name}`, { id: toastId });
          return null;
        }
      });

      const results = (await Promise.all(uploadPromises)).filter(
        (result) => result !== null
      );
      const urls = results.map((r) => r.url);

      // Add to files collection so CaseFiles page can find them
      const filesCollectionRef = collection(db, "files");
      for (const result of results) {
        try {
          await addDoc(filesCollectionRef, {
            caseNo: formData.caseNo,
            caseId: id,
            fileName: result.fileName,
            file_url: result.url,
            lawyer_uid: globalUser?.uid,
            uploadedAt: new Date().toISOString(),
          });
        } catch (err) {
          console.error("Error adding file document:", err);
        }
      }

      setFormData((prev) => ({
        ...prev,
        files: [...prev.files, ...urls],
      }));
    }
  };

  const isOwner = caseData?.owner === globalUser?.uid;
  const isShared = caseData?.team?.includes(globalUser?.uid);

  // Handler for reminder modal close (like Dart code line 1372-1373)
  const handleReminderModalClose = () => {
    setShowReminderModal(false);
    navigate(-1);
  };

  // Function to update events when next hearing date changes (like Dart code lines 884-1107)
  const updateEventsByCaseNumber = async (newNextHearingDateStr) => {
    try {
      console.log('=== EVENT UPDATE DEBUG (editcase) ===');
      console.log('Original Next Hearing Date:', originalNextHearingDate);
      console.log('New Next Hearing Date:', newNextHearingDateStr);

      // Check if next hearing date has changed
      if (originalNextHearingDate === newNextHearingDateStr) {
        console.log('❌ Next hearing date unchanged, skipping event update');
        return;
      }

      console.log('✅ Next hearing date changed, updating events...');

      const caseNo = formData.caseNo;
      const collectionName = getStatusCollection(caseData.status);

      // Delete all old events for this case
      const oldEventsQuery = query(
        collection(db, 'lawyers', globalUser.uid, 'events'),
        where('Case No', '==', caseNo)
      );
      const oldEventsSnapshot = await getDocs(oldEventsQuery);

      console.log(`Deleting ${oldEventsSnapshot.docs.length} old event(s)`);
      for (const docSnapshot of oldEventsSnapshot.docs) {
        await deleteDoc(docSnapshot.ref);
        console.log('  - Deleted old event:', docSnapshot.id);
      }

      // Delete old event reminders
      const oldRemindersQuery = query(
        collection(db, 'eventReminders'),
        where('caseNo', '==', caseNo),
        where('recipientId', '==', globalUser.uid)
      );
      const oldRemindersSnapshot = await getDocs(oldRemindersQuery);

      console.log(`Deleting ${oldRemindersSnapshot.docs.length} old reminder(s)`);
      for (const docSnapshot of oldRemindersSnapshot.docs) {
        await deleteDoc(docSnapshot.ref);
        console.log('  - Deleted old event reminder:', docSnapshot.id);
      }

      // Create new events if next hearing date exists
      if (!newNextHearingDateStr) {
        console.log('No next hearing date, skipping event creation');
        return;
      }

      // Parse the date from DD/MM/YYYY format
      const [day, month, year] = newNextHearingDateStr.split('/');
      const eventDate = new Date(year, month - 1, day);

      console.log('==========================================');
      console.log('Next Hearing Date Input (DD/MM/YYYY):', newNextHearingDateStr);
      console.log('Next Hearing Date (parsed Date object):', eventDate);
      console.log('Next Hearing Date (formatted YYYY-MM-DD):', formatDateToYYYYMMDD(eventDate));
      console.log('==========================================');

      // Event 1: 8:00 AM IST on the next hearing date (visible in calendar)
      const reminderDate = new Date(eventDate);
      reminderDate.setHours(8, 0, 0, 0); // 8:00 AM IST

      console.log('--- Event 1: Reminder (8 AM IST ONE DAY BEFORE) ---');
      console.log('Reminder Date (Date object):', reminderDate);
      console.log('Reminder Date (formatted YYYY-MM-DD):', formatDateToYYYYMMDD(reminderDate));
      console.log('Reminder Time (IST):', reminderDate.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
      console.log('Days difference from hearing date:', Math.floor((eventDate - reminderDate) / (1000 * 60 * 60 * 24)), 'day(s)');

      // Event 2: 6:00 PM IST on the next hearing date
      const eventTimeIST = new Date(eventDate);
      eventTimeIST.setHours(18, 0, 0, 0); // 6:00 PM IST

      console.log('--- Event 2: Main Event (6 PM IST ON HEARING DATE) ---');
      console.log('Event Date (Date object):', eventDate);
      console.log('Event Date (formatted YYYY-MM-DD):', formatDateToYYYYMMDD(eventDate));
      console.log('Event Time with 6 PM (Date object):', eventTimeIST);
      console.log('Event Time (IST):', eventTimeIST.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));

      // Create Event 1: Reminder at 8:00 AM one day before
      if (reminderDate > new Date()) {
        const reminderEventData = {
          'Case No': caseNo,
          date: formatDateToYYYYMMDD(reminderDate),
          mobile: userMobile || null,
          time: '08:00 AM',
          time1: reminderDate.getTime(),
          title: `${formData.petitionerName} VS ${formData.respondentName}`,
          description: `Hearing Reminder : Case No. - ${caseNo}`,
          isScheduled: reminderDate > new Date(),
          status: formData.status,
          eventType: 'reminder',
        };

        console.log('Creating Event 1 with data:', {
          date: reminderEventData.date,
          time: reminderEventData.time,
          title: reminderEventData.title,
          eventType: reminderEventData.eventType
        });
        console.log('Event 1 should be ONE DAY BEFORE hearing date');

        const reminderEventRef = await addDoc(
          collection(db, 'lawyers', globalUser.uid, 'events'),
          reminderEventData
        );

        console.log('✅ Event 1 created successfully with ID:', reminderEventRef.id);

        // Add to eventReminders collection
        const reminderDoc = {
          eventId: reminderEventRef.id,
          reminderTime: reminderDate,
          eventTitle: `Hearing Reminder : Case No. - ${caseNo}`,
          caseNo: caseNo,
          caseId: id,
          collection: collectionName,
          status: 'pending',
          createdAt: new Date(),
          scheduledBy: globalUser.uid,
          recipientId: globalUser.uid,
        };
        await addDoc(collection(db, 'eventReminders'), reminderDoc);
        console.log('✅ Event reminder added to eventReminders collection');
      } else {
        console.log('❌ Event 1 NOT created - reminderTime is in the past');
      }

      // Create Event 2: Main event at 6:00 PM on next hearing date
      if (eventTimeIST > new Date()) {
        const mainEventData = {
          'Case No': caseNo,
          date: formatDateToYYYYMMDD(eventDate),
          mobile: userMobile || null,
          time: '06:00 PM',
          time1: eventTimeIST.getTime(),
          title: `Update Next Hearing: ${formData.petitionerName} VS ${formData.respondentName}`,
          description: `Case No. - ${caseNo}`,
          isScheduled: eventTimeIST > new Date(),
          status: formData.status,
          eventType: 'updateReminder',
          isHidden: true,
        };

        console.log('Creating Event 2 with data:', {
          date: mainEventData.date,
          time: mainEventData.time,
          title: mainEventData.title,
          eventType: mainEventData.eventType
        });
        console.log('Event 2 should be ON THE SAME DAY as hearing date');

        const mainEventRef = await addDoc(
          collection(db, 'lawyers', globalUser.uid, 'events'),
          mainEventData
        );

        console.log('✅ Event 2 created successfully with ID:', mainEventRef.id);

        // Add to eventReminders collection
        const mainReminderDoc = {
          eventId: mainEventRef.id,
          reminderTime: eventTimeIST,
          eventTitle: `Update Next Hearing : ${caseNo}`,
          caseNo: caseNo,
          caseId: id,
          collection: collectionName,
          status: 'pending',
          createdAt: new Date(),
          scheduledBy: globalUser.uid,
          recipientId: globalUser.uid,
        };
        await addDoc(collection(db, 'eventReminders'), mainReminderDoc);
        console.log('✅ Event 2 reminder added to eventReminders collection');
      } else {
        console.log('❌ Event 2 NOT created - eventTime is in the past');
      }

      console.log('==========================================');
      console.log('=== EVENT UPDATE COMPLETED SUCCESSFULLY ===');
      console.log('Summary:');
      console.log('- Old events deleted');
      console.log('- New Event 1: 8 AM IST one day before hearing');
      console.log('- New Event 2: 6 PM IST on hearing date');
      console.log('==========================================\n');
    } catch (e) {
      console.error('❌ ERROR in updateEventsByCaseNumber:', e);
      console.error('Error details:', e.message);
      console.error('Error stack:', e.stack);
    }
  };



  const handleSaveChanges = async () => {
    if (!formData.respondentName?.trim()) {
      toast.error("Respondent name is required.");
      return;
    }
    try {
      const oldCollectionName = getStatusCollection(caseData.status);
      const newCollectionName = getStatusCollection(formData.status);
      const collectionName = newCollectionName;
      const caseDocRef = doc(db, newCollectionName, id);

      // Convert dates back to DD/MM/YYYY format for database storage
      let newNextHearingDate = convertToDisplayFormat(formData.nextHearingDate);
      let updatedPreviousHearingDate = convertToDisplayFormat(formData.previousHearingDate);

      // Get case history from caseData (like Dart code line 731-732)
      let caseHistory = Array.isArray(caseData?.caseHistory)
        ? [...caseData.caseHistory]
        : [];

      // Track if next hearing date was changed
      const nextHearingDateChanged = originalNextHearingDate !== newNextHearingDate;

      console.log('=== Case History Debug ===');
      console.log('Original Next (stored):', originalNextHearingDate);
      console.log('New Next (from form):', newNextHearingDate);
      console.log('Current Previous:', updatedPreviousHearingDate);
      console.log('History Length:', caseHistory.length);

      // Check if user has changed the next hearing date (like Dart code lines 754-816)
      if (originalNextHearingDate &&
          newNextHearingDate &&
          originalNextHearingDate !== newNextHearingDate) {
        try {
          // Parse the original date in DD/MM/YYYY format
          const [day, month, year] = originalNextHearingDate.split('/');
          const originalDate = new Date(year, month - 1, day);

          // Get today's date at midnight for comparison
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const originalDateOnly = new Date(originalDate);
          originalDateOnly.setHours(0, 0, 0, 0);

          console.log('Original Next Hearing Date:', originalDateOnly);
          console.log('Today:', today);
          console.log('Original is Past/Today:', originalDateOnly <= today);

          // If the ORIGINAL next hearing date is in the past or today (like Dart code lines 779-810)
          if (originalDateOnly <= today) {
            // Check if original date is not already in history
            const alreadyExists = caseHistory.some(
              (history) => history.hearingDate === originalNextHearingDate
            );

            if (!alreadyExists) {
              caseHistory.push({
                hearingDate: originalNextHearingDate,
                judge: 'N/A',
                purpose: 'N/A',
                causeListType: 'N/A',
                businessOnDate: 'N/A',
              });

              console.log('✓ Added to history:', originalNextHearingDate);
            } else {
              console.log('× Already exists in history');
            }

            // Update previous hearing date to the original (old) next hearing date
            updatedPreviousHearingDate = originalNextHearingDate;

            console.log('✓ Updated Previous Hearing Date to:', updatedPreviousHearingDate);
          } else {
            console.log('× Original date is in future, no history/previous update needed');
          }
        } catch (e) {
          console.error('× Parse error:', e);
        }
      } else {
        console.log('× Conditions not met (no change or empty dates)');
      }

      console.log('Final History Length:', caseHistory.length);
      console.log('Final Previous Hearing Date:', updatedPreviousHearingDate);
      console.log('========================');

      const {
        supremeCourtStage,
        supremeCourtCategory,
        supremeCourtSection,
        ...formDataRest
      } = formData;

      const isSC =
        formData.courtName === "Supreme Court" ||
        formData.courtName === "Supreme Court of India" ||
        caseData?.payload?.caseexportedfrom === "SupremeCourt";

      const dataToSave = {
        ...formDataRest,
        caseFiledDate: convertToDisplayFormat(formData.caseFiledDate),
        nextHearingDate: newNextHearingDate,
        previousHearingDate: updatedPreviousHearingDate,
        stayOrderDate: convertToDisplayFormat(formData.stayOrderDate),
        caseHistory: caseHistory,
        ...(isSC && {
          payload: {
            ...(caseData?.payload || {}),
            caseexportedfrom: caseData?.payload?.caseexportedfrom || "SupremeCourt",
            statusStage: supremeCourtStage || "",
            category: supremeCourtCategory || "",
            section: supremeCourtSection || "",
          },
        }),
      };

      if (oldCollectionName === newCollectionName) {
        await updateDoc(caseDocRef, dataToSave);
      } else {
        const oldDocRef = doc(db, oldCollectionName, id);
        const oldSnap = await getDoc(oldDocRef);
        const existingData = oldSnap.exists() ? oldSnap.data() : {};
        await setDoc(caseDocRef, { ...existingData, ...dataToSave });
        await deleteDoc(oldDocRef);
      }

      // Build changes object with old/new values for timeline
      const changes = {};
      if (formData.status !== caseData?.status) {
        changes["Case Status"] = { old: caseData?.status || "—", new: formData.status || "—" };
      }
      if (nextHearingDateChanged) {
        changes["Next Hearing Date"] = { old: originalNextHearingDate || "Not set", new: newNextHearingDate || "Not set" };
      }
      if (convertToDisplayFormat(formData.previousHearingDate) !== caseData?.previousHearingDate) {
        changes["Previous Hearing Date"] = { old: caseData?.previousHearingDate || "Not set", new: updatedPreviousHearingDate || "Not set" };
      }
      if (formData.respondentName !== caseData?.respondentName) {
        changes["Respondent Name"] = { old: caseData?.respondentName || "—", new: formData.respondentName || "—" };
      }
      if (formData.petitionerName !== caseData?.petitionerName) {
        changes["Petitioner Name"] = { old: caseData?.petitionerName || "—", new: formData.petitionerName || "—" };
      }
      if (formData.courtName !== caseData?.courtName) {
        changes["Court Name"] = { old: caseData?.courtName || "—", new: formData.courtName || "—" };
      }
      if (formData.appearingFor !== caseData?.appearingFor) {
        changes["Appearing For"] = { old: caseData?.appearingFor || "—", new: formData.appearingFor || "—" };
      }
      if (formData.caseType !== caseData?.caseType) {
        changes["Case Type"] = { old: caseData?.caseType || "—", new: formData.caseType || "—" };
      }

      const changedFieldNames = Object.keys(changes);
      addTimelineEvent(id, collectionName, {
        type: nextHearingDateChanged ? "hearing_updated" : "case_updated",
        title: nextHearingDateChanged ? "Hearing date updated" : "Case details updated",
        description: changedFieldNames.length > 0
          ? `Updated: ${changedFieldNames.join(", ")}`
          : "Case details were updated",
        performedBy: globalUser?.uid || "",
        performedByName: globalUser?.name || globalUser?.displayName || "",
        changes: changes,
      });

      // Update the original next hearing date for future comparisons (like Dart code line 1361)
      setOriginalNextHearingDate(newNextHearingDate);

      toast.success("Case updated successfully");

      // Handle event updates if next hearing date changed
      if (nextHearingDateChanged && globalUser?.uid) {
        await updateEventsByCaseNumber(newNextHearingDate);

        // Show default reminder info modal only if next hearing date was changed (like Dart code lines 1367-1369)
        setShowReminderModal(true);
      } else {
        // Navigate immediately if next hearing date didn't change
        navigate(-1);
      }
    } catch (error) {
      console.error("Error updating case:", error);
      toast.error("Failed to update case");
    }
  };

  const handleExportCase = async () => {
    try {
      // Check for duplicates
      const pendingQuery = query(
        collection(db, "pending"),
        where("owner", "==", globalUser.uid),
        where("caseNo", "==", formData.caseNo)
      );
      const disposedQuery = query(
        collection(db, "disposed"),
        where("owner", "==", globalUser.uid),
        where("caseNo", "==", formData.caseNo)
      );
      const [pendingSnapshot, disposedSnapshot] = await Promise.all([
        getDocs(pendingQuery),
        getDocs(disposedQuery),
      ]);

      if (!pendingSnapshot.empty || !disposedSnapshot.empty) {
        toast.error("This case is already saved in your cases.");
        return;
      }

      // Create new case
      const collectionName = getStatusCollection(formData.status);
      const casesCollectionRef = collection(db, collectionName);
      const newCaseData = {
        ...formData,
        owner: globalUser.uid,
        team: [globalUser.uid],
      };
      const newDocRef = await addDoc(casesCollectionRef, newCaseData);

      // Create event if nextHearingDate exists
      if (formData.nextHearingDate) {
        try {
          const eventDate = new Date(
            formData.nextHearingDate.split("/").reverse().join("-")
          );

          // Event 1: 8:00 AM IST on the next hearing date (visible in calendar)
          const reminderDate = new Date(eventDate);
          reminderDate.setHours(8, 0, 0, 0);

          // Event 2: 6:00 PM on the next hearing date
          const eventTimeIST = new Date(eventDate);
          eventTimeIST.setHours(18, 0, 0, 0);

          // Create Event 1: Reminder at 8:00 AM one day before
          if (reminderDate > new Date()) {
            const reminderEventData = {
              "Case No": formData.caseNo,
              date: formatDateToYYYYMMDD(reminderDate),
              id: Math.floor(Math.random() * 90000) + 10000,
              mobile: userMobile || null,
              time: "08:00 AM",
              time1: reminderDate.getTime(),
              title: `${formData.petitionerName} VS ${formData.respondentName}`,
              description: `Hearing Reminder : Case No. - ${formData.caseNo}`,
              isScheduled: reminderDate > new Date(),
              status: formData.status,
              eventType: "reminder",
            };
            const reminderEventRef = await addDoc(
              collection(db, "lawyers", globalUser.uid, "events"),
              reminderEventData
            );

            // Add to eventReminders collection
            const reminderDoc = {
              eventId: reminderEventRef.id,
              reminderTime: reminderDate,
              eventTitle: `Hearing Reminder : Case No. - ${formData.caseNo}`,
              caseNo: formData.caseNo,
              status: "pending",
              createdAt: new Date(),
              scheduledBy: globalUser.uid,
              recipientId: globalUser.uid,
            };
            await addDoc(collection(db, "eventReminders"), reminderDoc);
          }

          // Create Event 2: Main event at 6:00 PM on next hearing date
          if (eventTimeIST > new Date()) {
            const mainEventData = {
              "Case No": formData.caseNo,
              date: formatDateToYYYYMMDD(eventDate),
              id: Math.floor(Math.random() * 90000) + 10000,
              mobile: userMobile || null,
              time: "06:00 PM",
              time1: eventTimeIST.getTime(),
              title: `Update Next Hearing: ${formData.petitionerName} VS ${formData.respondentName}`,
              description: `Case No. - ${formData.caseNo}`,
              isScheduled: eventTimeIST > new Date(),
              status: formData.status,
              eventType: "updateReminder",
              isHidden: true,
            };
            const mainEventRef = await addDoc(
              collection(db, "lawyers", globalUser.uid, "events"),
              mainEventData
            );

            // Add to eventReminders collection
            const mainReminderDoc = {
              eventId: mainEventRef.id,
              reminderTime: eventTimeIST,
              eventTitle: `Update Next Hearing : ${formData.caseNo}`,
              caseNo: formData.caseNo,
              status: "pending",
              createdAt: new Date(),
              scheduledBy: globalUser.uid,
              recipientId: globalUser.uid,
            };
            await addDoc(collection(db, "eventReminders"), mainReminderDoc);
          }
        } catch (error) {
          console.error("Error creating event:", error);
        }
      }

      // Remove the user from the original case's team
      const originalCollectionName = getStatusCollection(caseData.status);
      const originalCaseDocRef = doc(db, originalCollectionName, id);
      const originalCaseSnapshot = await getDoc(originalCaseDocRef);
      if (originalCaseSnapshot.exists()) {
        const originalData = originalCaseSnapshot.data();
        const updatedTeam = originalData.team.filter(uid => uid !== globalUser.uid);
        await updateDoc(originalCaseDocRef, { team: updatedTeam });
      }

      toast.success("Case exported successfully.");
      navigate("/portal/Dashboard");
    } catch (error) {
      console.error("Error exporting case:", error);
      toast.error("Failed to export case");
    }
  };

  const handleSave = async () => {
    if (!globalUser) {
      toast.error("User not authenticated");
      return;
    }

    if (caseData.owner === globalUser.uid) {
      // Case is owned by the user, update as before
      try {
        const collectionName =
          caseData.status === "Pending" ? "pending" : "disposed";
        const caseDocRef = doc(db, collectionName, id);
        await updateDoc(caseDocRef, formData);
        toast.success("Case updated successfully");
        navigate(-1);
      } catch (error) {
        console.error("Error updating case:", error);
        toast.error("Failed to update case");
      }
    } else {
      // Case is shared, check for duplicates and create new case
      try {
        const pendingQuery = query(
          collection(db, "pending"),
          where("owner", "==", globalUser.uid),
          where("caseNo", "==", formData.caseNo)
        );
        const disposedQuery = query(
          collection(db, "disposed"),
          where("owner", "==", globalUser.uid),
          where("caseNo", "==", formData.caseNo)
        );
        const [pendingSnapshot, disposedSnapshot] = await Promise.all([
          getDocs(pendingQuery),
          getDocs(disposedQuery),
        ]);

        if (!pendingSnapshot.empty || !disposedSnapshot.empty) {
          toast.error("This case is already saved in your cases.");
        } else {
          // Create new case
          const collectionName =
            formData.status === "Pending" ? "pending" : "disposed";
          const casesCollectionRef = collection(db, collectionName);
          const newCaseData = {
            ...formData,
            owner: globalUser.uid,
            team: [globalUser.uid], // Initialize team with only the current user
          };
          const newDocRef = await addDoc(casesCollectionRef, newCaseData);

          // Create event if nextHearingDate exists
          if (formData.nextHearingDate) {
            try {
              const eventDate = new Date(
                formData.nextHearingDate.split("/").reverse().join("-")
              );
              await addDoc(
                collection(db, "lawyers", globalUser.uid, "events"),
                {
                  "Case No": formData.caseNo,
                  date: eventDate.toISOString().split("T")[0],
                  id: Math.floor(Math.random() * 90000) + 10000,
                  mobile: userMobile || null,
                  time: "08:00 AM",
                  time1: eventDate.getTime(),
                  title: `${formData.petitionerName} VS ${formData.respondentName}`,
                  isScheduled: eventDate > new Date(),
                  status: formData.status,
                }
              );
            } catch (error) {
              console.error("Error creating event:", error);
            }
          }
          toast.success("Case added to your cases successfully.");
          navigate("/portal/Dashboard");
        }
      } catch (error) {
        console.error("Error saving case:", error);
        toast.error("Failed to save case");
      }
    }
  };

  return (
    <div>
      <Navbar />
      <div className="p-4 md:p-12">
        <main className="px-4 md:px-16 py-8">
          <div className="flex items-center mb-6">
            <Link
              to={`/case/${id}`}
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white"
            >
              <div className="flex items-center md:ml-12">
                <ArrowLeft className="portal-back-arrow" style={{ color: "white" }} />
                <h1 className="text-yellow-500 ml-4 font-bold text-2xl">
                  Edit Case Details
                </h1>
              </div>
            </Link>
          </div>

          <div className="md:flex md:items-end md:justify-end md:py-4 md:px-52 flex items-end justify-end py-4">
            <div className="space-x-4">
              <button
                onClick={() =>
                  navigate("/portal/editcase-notes", {
                    state: {
                      caseData: { ...caseData, id },
                      from: location.pathname,
                    },
                  })
                }
                className="bg-transparent text-white border-x-2 border-y-2 px-4 py-2 rounded-lg"
              >
                Edit Notes
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-yellow-500 text-black px-4 py-2 rounded-lg"
              >
                Upload File
              </button>
              {/* {isOwner && (
                <button
                  className="bg-yellow-500 text-black px-4 py-2 rounded-lg"
                  onClick={confirmSaveAfterEdit}
                >
                  Save
                </button>
              )} */}

              {(isOwner || isShared) && (
                <button
                  className="bg-yellow-500 text-black px-4 py-2 rounded-lg"
                  onClick={handleSaveChanges}
                >
                  Save Changes
                </button>
              )}
              {isShared && !isOwner && (
                <button
                  className="light-button-text bg-blue-500 text-white px-4 py-2 rounded-lg"
                  onClick={handleExportCase}
                >
                  Export Case
                </button>
              )}
            </div>
          </div>

          <div className="mx-auto max-w-4xl space-y-8">
          <div className="bg-[#1E1E45] rounded-lg p-6 md:p-12">
            <h2 className="text-lg font-semibold text-yellow-500 mb-6 pb-3 border-b border-gray-700">Case Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Case Number */}
              <div>
                <label className="block text-gray-400 mb-1">
                  Case Number
                </label>
                <div className="w-full bg-gray-700 rounded-lg py-2 px-4 text-gray-300 text-sm cursor-not-allowed min-h-[38px]">
                  {formData.caseNo || "—"}
                </div>
              </div>

              {/* Case Name (Type) */}
              <div>
                <label className="block text-gray-400 mb-1">Case Name</label>
                <div className="relative">
                  <input
                    type="text"
                    name="caseType"
                    value={formData.caseType}
                    onChange={handleInputChange}
                    className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                  />
                  <Pencil className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                </div>
              </div>

              {/* Case Year */}
              <div>
                <label className="block text-gray-400 mb-1">Case Year</label>
                <div className="relative edit-case-year-picker">
                  <DatePicker
                    selected={formData.caseYear ? new Date(formData.caseYear, 0, 1) : null}
                    onChange={(date) => {
                      const year = date ? date.getFullYear().toString() : "";
                      setFormData((prev) => ({ ...prev, caseYear: year }));
                    }}
                    showYearPicker
                    dateFormat="yyyy"
                    placeholderText="Select or enter case year"
                    yearItemNumber={9}
                    className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                    wrapperClassName="w-full"
                    calendarClassName="bg-gray-900"
                  />
                  <Calendar className="absolute right-3 top-2.5 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Court Name */}
              <div>
                <label className="block text-gray-400 mb-1">Court Name</label>
                <div className="relative">
                  <input
                    type="text"
                    name="courtName"
                    value={formData.courtName}
                    onChange={handleInputChange}
                    className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                  />
                  <Pencil className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                </div>
              </div>

              {/* Case Status */}
              <div>
                <label className="block text-gray-400 mb-1">
                  Case Status <span className="text-red-500">*</span>
                </label>
                <SelectMenu
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white"
                  required
                >
                  <option value="">Choose Status</option>
                  {CASE_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.value}
                    </option>
                  ))}
                </SelectMenu>
              </div>

              {/* Case Stage */}
              <div>
                <label className="block text-gray-400 mb-1">Case Stage</label>
                <SelectMenu
                  name="caseStage"
                  value={formData.caseStage}
                  onChange={handleInputChange}
                  className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white"
                >
                  <option value="">Choose Stage</option>
                  {CASE_STAGES.map((stage) => (
                    <option key={stage} value={stage}>
                      {stage}
                    </option>
                  ))}
                </SelectMenu>
              </div>

              {/* Supreme Court extras - Stage, Category, Section */}
              {(formData.courtName === "Supreme Court" ||
                formData.courtName === "Supreme Court of India" ||
                caseData?.payload?.caseexportedfrom === "SupremeCourt") && (
                <>
                  <div>
                    <label className="block text-gray-400 mb-1">Status / Stage</label>
                    <div className="relative">
                      <input
                        type="text"
                        name="supremeCourtStage"
                        value={formData.supremeCourtStage}
                        onChange={handleInputChange}
                        className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                        placeholder="Enter Status / Stage"
                      />
                      <Pencil className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-1">Category</label>
                    <div className="relative">
                      <input
                        type="text"
                        name="supremeCourtCategory"
                        value={formData.supremeCourtCategory}
                        onChange={handleInputChange}
                        className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                        placeholder="Enter Category"
                      />
                      <Pencil className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-1">Section</label>
                    <div className="relative">
                      <input
                        type="text"
                        name="supremeCourtSection"
                        value={formData.supremeCourtSection}
                        onChange={handleInputChange}
                        className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                        placeholder="Enter Section"
                      />
                      <Pencil className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </>
              )}

              {/* Party Contact Number */}
              <div>
                <label className="block text-gray-400 mb-1">
                  Party Contact Number
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="partyContactNo"
                    value={formData.partyContactNo}
                    onChange={handleInputChange}
                    className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                  />
                  <Pencil className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                </div>
              </div>

              {/* Case Filed Date */}
              <div>
                <label className="block text-gray-400 mb-1">
                  Case Filed Date
                </label>
                <div className="relative edit-case-date-picker">
                  <DatePicker
                    selected={parseDate(formData.caseFiledDate)}
                    onChange={handleCaseFiledDateChange}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select case filed date"
                    className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                    popperPlacement="bottom-start"
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
              </div>

              {/* Previous Hearing Date */}
              <div>
                <label className="block text-gray-400 mb-1">
                  Previous Hearing Date
                </label>
                <div className="relative edit-case-date-picker">
                  <DatePicker
                    selected={parseDate(formData.previousHearingDate)}
                    onChange={handlePreviousHearingDateChange}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select previous hearing date"
                    className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                    popperPlacement="bottom-start"
                    maxDate={new Date()}
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
              </div>

              {/* Next Hearing Date */}
              <div>
                <label className="block text-gray-400 mb-1">
                  Next Hearing Date
                </label>
                <div className="relative edit-case-date-picker">
                  <DatePicker
                    selected={parseDate(formData.nextHearingDate)}
                    onChange={handleNextHearingDateChange}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select next hearing date"
                    minDate={parseDate(formData.previousHearingDate) || todayDate}
                    className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                    popperPlacement="bottom-start"
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
              </div>

              {/* Authority Department (gated by department-access verification) */}
              {isDepartmentVerified && (
              <div>
                <label className="block text-gray-400 mb-1">
                  Authority Department
                </label>
                <CustomSelect
                  value={formData.authorityDepartment}
                  onChange={(val) =>
                    setFormData((prev) => ({
                      ...prev,
                      authorityDepartment: val,
                      ...(val !== "Land" ? { village: "" } : {}),
                      ...(val !== "Others" ? { authorityDepartmentOther: "" } : {}),
                    }))
                  }
                  options={AUTHORITY_DEPARTMENTS}
                  placeholder="Choose authority department"
                />
              </div>
              )}

              {isDepartmentVerified && formData.authorityDepartment === "Others" && (
                <div>
                  <label className="block text-gray-400 mb-1">
                    Please specify authority department
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="authorityDepartmentOther"
                      value={formData.authorityDepartmentOther}
                      onChange={handleInputChange}
                      className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                      placeholder="Enter authority department"
                    />
                    <Pencil className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                  </div>
                </div>
              )}

              {isDepartmentVerified && formData.authorityDepartment && (
                <>
                  {/* Properties */}
                  <div>
                    <label className="block text-gray-400 mb-1">Properties</label>
                    <div className="relative">
                      <input
                        type="text"
                        name="properties"
                        value={formData.properties}
                        onChange={handleInputChange}
                        className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                        placeholder="Enter properties"
                      />
                      <Pencil className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                    </div>
                  </div>

                  {/* Area */}
                  <div>
                    <label className="block text-gray-400 mb-1">Area</label>
                    <div className="relative">
                      <input
                        type="text"
                        name="area"
                        value={formData.area}
                        onChange={handleInputChange}
                        className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                        placeholder="Enter area"
                      />
                      <Pencil className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </>
              )}

              {/* Village (only if Land) */}
              {isDepartmentVerified && formData.authorityDepartment === "Land" && (
                <div>
                  <label className="block text-gray-400 mb-1">Village / Division</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="village"
                      value={formData.village}
                      onChange={handleInputChange}
                      className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                      placeholder="Enter village"
                    />
                    <Pencil className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                  </div>
                </div>
              )}

              {/* Stay Order Date */}
              <div>
                <label className="block text-gray-400 mb-1">Stay Order Date</label>
                <div className="relative edit-case-date-picker">
                  <DatePicker
                    selected={parseDate(formData.stayOrderDate)}
                    onChange={(date) =>
                      setFormData((prev) => ({
                        ...prev,
                        stayOrderDate: formatDateToInput(date),
                      }))
                    }
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select stay order date"
                    className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                    popperPlacement="bottom-start"
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
              </div>

              {/* Status of Counter Affidavit */}
              <div>
                <label className="block text-gray-400 mb-1">
                  Status of Counter Affidavit
                </label>
                <CustomSelect
                  value={formData.counterAffidavitStatus}
                  onChange={(val) =>
                    setFormData((prev) => ({ ...prev, counterAffidavitStatus: val }))
                  }
                  options={COUNTER_AFFIDAVIT_STATUSES}
                  placeholder="Choose status"
                />
              </div>

              {/* Counter Appeal Filed */}
              <div>
                <span aria-hidden="true" className="block mb-1 invisible select-none">.</span>
                <div className="flex items-center gap-6 flex-wrap h-[42px]">
                  <label className="text-gray-400">
                    Counter Appeal Filed
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer text-white">
                    <input
                      type="radio"
                      name="counterAppealFiled"
                      value="Yes"
                      checked={formData.counterAppealFiled === "Yes"}
                      onChange={() =>
                        setFormData((prev) => ({ ...prev, counterAppealFiled: "Yes" }))
                      }
                      className="accent-yellow-500"
                    />
                    <span>Yes</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer text-white">
                    <input
                      type="radio"
                      name="counterAppealFiled"
                      value="No"
                      checked={formData.counterAppealFiled === "No"}
                      onChange={() =>
                        setFormData((prev) => ({ ...prev, counterAppealFiled: "No" }))
                      }
                      className="accent-yellow-500"
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>

            </div>

          </div>

          <div className="bg-[#1E1E45] rounded-lg p-6 md:p-12">
            <h2 className="text-lg font-semibold text-yellow-500 mb-6 pb-3 border-b border-gray-700">Parties & Representation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Petitioner */}
              <div>
                <label className="block text-gray-400 mb-1">
                  Petitioner Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="petitionerName"
                    value={formData.petitionerName}
                    onChange={handleInputChange}
                    className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                  />
                  <Pencil className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                </div>
                <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                  {formData.allPetitioners.length > 0 ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-300">+{formData.allPetitioners.length} Petitioners</span>
                      <button
                        type="button"
                        onClick={() => setShowPetitionerModal(true)}
                        className="text-xs bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 px-2 py-0.5 rounded transition-colors"
                      >
                        View
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowPetitionerModal(true)}
                      className="text-sm text-yellow-500 hover:text-yellow-400 transition-colors whitespace-nowrap"
                    >
                      + Add More
                    </button>
                  )}
                  <RelationBadge
                    relation={formData.petitionerRelation}
                    relationName={formData.petitionerRelationName}
                    onOpen={() => setShowPetitionerRelationDialog(true)}
                    onClear={() => setFormData(prev => ({ ...prev, petitionerRelation: "", petitionerRelationName: "" }))}
                  />
                </div>
              </div>

              {/* Petitioner Advocate */}
              <div>
                <label className="block text-gray-400 mb-1">
                  Petitioner Advocate
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="petitionerAdvocate"
                    value={formData.petitionerAdvocate}
                    onChange={handleInputChange}
                    className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                  />
                  <Pencil className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                </div>
                {formData.allPetitionerAdvocates.length > 0 ? (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm text-gray-300">+{formData.allPetitionerAdvocates.length} Petitioner Advocates</span>
                    <button
                      type="button"
                      onClick={() => setShowPetitionerAdvocateModal(true)}
                      className="text-xs bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 px-2 py-0.5 rounded transition-colors"
                    >
                      View
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowPetitionerAdvocateModal(true)}
                    className="mt-2 text-sm text-yellow-500 hover:text-yellow-400 transition-colors"
                  >
                    + Add More Petitioner Advocates
                  </button>
                )}
              </div>

              {/* Respondent */}
              <div>
                <label className="block text-gray-400 mb-1">
                  Respondent Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="respondentName"
                    value={formData.respondentName}
                    onChange={handleInputChange}
                    className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                    required
                  />
                  <Pencil className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                </div>
                <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                  {formData.allRespondents.length > 0 ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-300">+{formData.allRespondents.length} Respondents</span>
                      <button
                        type="button"
                        onClick={() => setShowRespondentModal(true)}
                        className="text-xs bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 px-2 py-0.5 rounded transition-colors"
                      >
                        View
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowRespondentModal(true)}
                      className="text-sm text-yellow-500 hover:text-yellow-400 transition-colors whitespace-nowrap"
                    >
                      + Add More
                    </button>
                  )}
                  <RelationBadge
                    relation={formData.respondentRelation}
                    relationName={formData.respondentRelationName}
                    onOpen={() => setShowRespondentRelationDialog(true)}
                    onClear={() => setFormData(prev => ({ ...prev, respondentRelation: "", respondentRelationName: "" }))}
                  />
                </div>
              </div>

              {/* Respondent Advocate */}
              <div>
                <label className="block text-gray-400 mb-1">
                  Respondent Advocate
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="respondentAdvocate"
                    value={formData.respondentAdvocate}
                    onChange={handleInputChange}
                    className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white pr-10"
                  />
                  <Pencil className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                </div>
                {formData.allRespondentAdvocates.length > 0 ? (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm text-gray-300">+{formData.allRespondentAdvocates.length} Respondent Advocates</span>
                    <button
                      type="button"
                      onClick={() => setShowRespondentAdvocateModal(true)}
                      className="text-xs bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 px-2 py-0.5 rounded transition-colors"
                    >
                      View
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowRespondentAdvocateModal(true)}
                    className="mt-2 text-sm text-yellow-500 hover:text-yellow-400 transition-colors"
                  >
                    + Add More Respondent Advocates
                  </button>
                )}
              </div>

              {/* Prayers */}
              <div className="md:col-span-2">
                <label className="block text-gray-400 mb-1">Prayers</label>
                <textarea
                  name="prayers"
                  value={formData.prayers || ""}
                  onChange={handleInputChange}
                  rows={1}
                  className="w-full bg-gray-900 rounded-lg py-2 px-4 text-white resize-y whitespace-pre-wrap break-words"
                  placeholder="Enter prayers / reliefs sought"
                />
              </div>
            </div>
          </div>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            multiple
            className="hidden"
          />
          </div>
          {/* {showPopup && (
            <SavePopup onConfirm={confirmSave} onCancel={cancelSave} />
          )} */}
        </main>
      </div>

      {/* Add More Modals */}
      <AddMoreModal
        isOpen={showPetitionerModal}
        onClose={() => setShowPetitionerModal(false)}
        title="Add More Petitioners"
        items={formData.allPetitioners}
        setItems={(items) => setFormData(prev => ({ ...prev, allPetitioners: items }))}
        supportsRelation
      />
      <AddMoreModal
        isOpen={showRespondentModal}
        onClose={() => setShowRespondentModal(false)}
        title="Add More Respondents"
        items={formData.allRespondents}
        setItems={(items) => setFormData(prev => ({ ...prev, allRespondents: items }))}
        supportsRelation
      />
      <AddMoreModal
        isOpen={showPetitionerAdvocateModal}
        onClose={() => setShowPetitionerAdvocateModal(false)}
        title="Add More Petitioner Advocates"
        items={formData.allPetitionerAdvocates}
        setItems={(items) => setFormData(prev => ({ ...prev, allPetitionerAdvocates: items }))}
      />
      <AddMoreModal
        isOpen={showRespondentAdvocateModal}
        onClose={() => setShowRespondentAdvocateModal(false)}
        title="Add More Respondent Advocates"
        items={formData.allRespondentAdvocates}
        setItems={(items) => setFormData(prev => ({ ...prev, allRespondentAdvocates: items }))}
      />

      {/* Default Reminder Info Modal */}
      <DefaultReminderInfoModal
        isOpen={showReminderModal}
        onClose={handleReminderModalClose}
      />

      <RelationDialog
        isOpen={showPetitionerRelationDialog}
        onClose={() => setShowPetitionerRelationDialog(false)}
        title={`Set Relation${formData.petitionerName ? ` — ${formData.petitionerName}` : ""}`}
        relation={formData.petitionerRelation}
        relationName={formData.petitionerRelationName}
        onSave={({ relation, relationName }) =>
          setFormData(prev => ({ ...prev, petitionerRelation: relation, petitionerRelationName: relationName }))
        }
        namePlaceholder="Enter father/mother name"
      />
      <RelationDialog
        isOpen={showRespondentRelationDialog}
        onClose={() => setShowRespondentRelationDialog(false)}
        title={`Set Relation${formData.respondentName ? ` — ${formData.respondentName}` : ""}`}
        relation={formData.respondentRelation}
        relationName={formData.respondentRelationName}
        onSave={({ relation, relationName }) =>
          setFormData(prev => ({ ...prev, respondentRelation: relation, respondentRelationName: relationName }))
        }
        namePlaceholder="Enter father/mother name"
      />
    </div>
  );
}

export default EditCaseDetails;
